import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../App";
import { useRoleContext } from "../../hooks/useRoleContext";
import API from "../../utils/api";
import AddVisit from "./AddVisit";
import AddLead from "./AddLead";
import MyLeads from "./MyLeads";
import FollowUps from "./FollowUps";
import SalesLearning from "../../components/SalesLearning";
import TargetCenter from "../../components/TargetCenter";
import MapView from "../../components/MapView";
import BulkMessage from "./BulkMessage";
import Profile from "./Profile";
import DailyProductivity from "../DailyProductivity";
import DailyVisitReport from "../../components/DailyVisitReport";
import CRMContextBanner from "../../components/CRMContextBanner";
import WhatsAppCenter from "../admin/WhatsAppCenter";
import EmailCenter from "../admin/EmailCenter";
import { redirectWithSsoCode } from "../../utils/ssoRedirect";

const HRMS_URL = process.env.REACT_APP_HRMS_URL || "http://localhost:5000";

const EXPENSE_URL     = process.env.REACT_APP_EXPENSE_URL     || "http://localhost:4000";
const EXPENSE_API_URL = process.env.REACT_APP_EXPENSE_API_URL || "http://localhost:4000/api";

function AppSwitcher({ currentApp = "crm", allowedApps = ["hrms", "crm", "expense"] }) {
  const [busy, setBusy] = React.useState(null);
  const apps = [
    { key: "hrms",    label: "HRMS",    icon: "👤" },
    { key: "crm",     label: "CRM",     icon: "📊" },
    { key: "expense", label: "Expense", icon: "💰" },
  ].filter(a => allowedApps.includes(a.key));
  const navigate = async (appKey) => {
    if (appKey === currentApp) return;
    const hrmsToken = localStorage.getItem("hrms_token") || localStorage.getItem("crm_token");
    if (!hrmsToken) { window.location.href = HRMS_URL + "/login"; return; }
    setBusy(appKey);
    try {
      if (appKey === "hrms") {
        await redirectWithSsoCode({ targetUrl: HRMS_URL, token: hrmsToken });
        return;
      }
      if (appKey === "expense") {
        await redirectWithSsoCode({ targetUrl: EXPENSE_URL, token: hrmsToken, hashRoute: true });
      }
    } catch { setBusy(null); }
  };
  return (
    <div style={{ padding: "10px 12px 12px", borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: "auto" }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Switch App</div>
      <div style={{ display: "flex", gap: 4 }}>
        {apps.map(app => (
          <button key={app.key}
            onClick={() => navigate(app.key)}
            disabled={busy === app.key}
            title={app.label}
            style={{
              flex: 1, padding: "5px 2px", border: "1.5px solid",
              borderColor: app.key === currentApp ? "#ef4444" : "rgba(255,255,255,0.2)",
              borderRadius: 7, background: app.key === currentApp ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.06)",
              color: app.key === currentApp ? "#ef4444" : "rgba(255,255,255,0.7)",
              fontSize: 9, fontWeight: 800, fontFamily: "inherit",
              cursor: app.key === currentApp ? "default" : "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
            }}>
            <span style={{ fontSize: 13 }}>{busy === app.key ? "…" : app.icon}</span>
            {app.label}
          </button>
        ))}
      </div>
    </div>
  );
}



const PLAN_LABELS = {
  institution_visit: { label: "Field Visit",        color: "#ef1f35", short: "FV",  emoji: "" },
  office:            { label: "Work from Office",   color: "#2874f0", short: "WFO", emoji: "" },
  wfh:               { label: "Work from Home",     color: "#079b9b", short: "WFH", emoji: "" },
  official_travel:   { label: "Official Travel",    color: "#0ea5e9", short: "TR",  emoji: "🚗" },
  exam_duty:         { label: "Exam Duty",          color: "#f09b22", short: "EX",  emoji: "" },
  planned_leave:     { label: "Leave",              color: "#8b5cf6", short: "LV",  emoji: "" },
};

// Base nav for all employees; admin/superadmin get extra tabs added inside the component
const BASE_NAV = [
  { key: "home",         icon: "", label: "Home" },
  { key: "visits",       icon: "", label: "Visits" },
  { key: "addlead",      icon: "", label: "Add Lead" },
  { key: "followups",    icon: "", label: "Follow-ups" },
  { key: "dayreport",    icon: "📋", label: "Team Day Report" },
  { key: "leads",        icon: "", label: "Leads" },
  { key: "productivity", icon: "📊", label: "My Productivity" },
  { key: "targets",      icon: "",  label: "Targets" },
  { key: "learning",     icon: "", label: "Learning" },
  { key: "email",        icon: "✉️", label: "Email" },
  { key: "whatsapp",     icon: "💬", label: "WhatsApp" },
  { key: "map",          icon: "🗺",  label: "Map" },
  { key: "bulk",         icon: "💬", label: "Blast" },
];
const ADMIN_EXTRA_NAV = [];

const MOBILE_PRIMARY_KEYS = ["home", "visits", "followups", "leads"];
const NAV_ICONS = {
  home: "HM",
  visits: "VS",
  addlead: "AL",
  followups: "FU",
  dayreport: "DR",
  leads: "LD",
  map: "MP",
  bulk: "BL",
  targets: "TG",
  learning: "LR",
  profile: "PR",
  wall: "🏆",
  more: "MN",
  logout: "LO",
};

function getNavIcon(item) {
  return NAV_ICONS[item.key] || item.icon || item.label.slice(0, 2).toUpperCase();
}

const MOTIVATION = [
  "Every visit is a new opportunity. Keep moving. ",
  "Small follow-ups become big wins. Stay sharp. ",
  "Your route, your rhythm, your results. ",
  "Great sales days start with one clear plan. ",
  "Build trust today. Revenue follows. ",
];

const COMPANY_BRANDS = {
  ebek: {
    label: "Ebek",
    logo: "/logo-ebek-crm.png",
    accent: "#ef1f35",
    secondary: "#079b9b",
    primaryGlow: "rgba(239, 31, 53, 0.08)",
    accentShadow: "rgba(239, 31, 53, 0.27)",
  },
  ebekmed: {
    label: "Ebekmed",
    logo: "/logo-ebekmed-skills.png",
    accent: "#079b9b",
    secondary: "#ef1f35",
    primaryGlow: "rgba(7, 155, 155, 0.12)",
    accentShadow: "rgba(7, 155, 155, 0.30)",
  },
};

function todayIST() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function formatTime(value) {
  if (!value) return "--";
  return new Date(value).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function getDistanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function EmployeeDashboard() {
  const { user, logout } = useAuth();
  const { allowedApps } = useRoleContext(user, "crm");
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const quickVisit = sessionStorage.getItem("crm_quick_visit") === "1";
    if (quickVisit) sessionStorage.removeItem("crm_quick_visit");
    return quickVisit ? "visits" : "home";
  });
  const [today, setToday] = useState(null);
  const [todayVisits, setTodayVisits] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notifRef = useRef(null);

  const firstName = user?.name?.split(" ")[0] || "there";
  const company = user?.company || "ebek";
  const brand = COMPANY_BRANDS[company] || COMPANY_BRANDS.ebek;
  const accent = brand.accent;
  const checkin = today?.checkin;
  const scheduled = today?.scheduled;
  const plan = checkin ? PLAN_LABELS[checkin.plan_type] : null;
  const effectiveRole = user?.role === "superadmin"
    ? "superadmin"
    : (user?.app_role_overrides?.crm || user?.role);
  const canSeeTeamDayReport = ["manager", "team_leader", "superadmin"].includes(effectiveRole);
  const isAdminOrSuper = ["admin","superadmin"].includes(effectiveRole);
  const roleNav = isAdminOrSuper ? [...BASE_NAV, ...ADMIN_EXTRA_NAV] : BASE_NAV;
  const NAV = roleNav.filter(item => item.key !== "dayreport" || canSeeTeamDayReport);
  const mobilePrimaryNav = NAV.filter(item => MOBILE_PRIMARY_KEYS.includes(item.key));
  const mobileMoreNav = NAV.filter(item => !MOBILE_PRIMARY_KEYS.includes(item.key));
  const isMoreActive = mobileMoreNav.some(item => item.key === tab) || tab === "profile";

  const motivation = useMemo(() => {
    const index = new Date().getDate() % MOTIVATION.length;
    return MOTIVATION[index];
  }, []);

  const dueFollowups = useMemo(() => {
    const todayString = todayIST();
    return followups.filter(f => f.status === "scheduled" && f.scheduled_date <= todayString);
  }, [followups]);

  const fetchHome = useCallback(async () => {
    try {
      setLoading(true);
      const [todayRes, visitsRes, followRes, statsRes] = await Promise.all([
        API.get("/checkins/today"),
        API.get("/visits/today"),
        API.get("/followups/mine"),
        API.get("/visits/stats/summary"),
      ]);
      setToday(todayRes.data);
      setTodayVisits(visitsRes.data || []);
      setFollowups(followRes.data || []);
      setStats(statsRes.data || null);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await API.get("/notifications");
      setNotifications(res.data.notifications || []);
      setNotifCount(res.data.count || 0);
    } catch {
      // Background refresh only.
    }
  }, []);

  useEffect(() => { fetchHome(); }, [fetchHome]);

  useEffect(() => {
    window.addEventListener("sales-attendance-marked", fetchHome);
    return () => window.removeEventListener("sales-attendance-marked", fetchHome);
  }, [fetchHome]);
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    function close(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    if (notifOpen) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [notifOpen]);

  const handleLogout = () => { logout(); };

  const handleCheckout = async () => {
    try {
      await API.put("/checkins/checkout");
      toast.success("Logged out for today. Great work! ");
      await fetchHome();
    } catch (err) {
      toast.error(err.response?.data?.error || "Checkout failed");
    }
  };

  const markAllRead = async () => {
    try {
      await API.put("/notifications/read-all");
      await fetchNotifications();
    } catch {
      toast.error("Could not update notifications");
    }
  };

  const openNotification = (n) => {
    if (n.stored && n.dbId) API.put(`/notifications/${n.dbId}/read`).catch(() => {});
    setNotifOpen(false);
    if (["followup_overdue", "followup_tomorrow", "team_overdue"].includes(n.type)) setTab("followups");
    if (["payment_pending", "mou_pending"].includes(n.type)) setTab("leads");
  };

  const setActiveTab = (nextTab) => {
    setTab(nextTab);
    setMobileMenuOpen(false);
    setSidebarOpen(false);
  };

  useEffect(() => {
    const continueDraft = () => setActiveTab("visits");
    window.addEventListener("crm-open-visit-draft", continueDraft);
    return () => window.removeEventListener("crm-open-visit-draft", continueDraft);
  }, []);

  const renderActiveTab = () => {
    if (tab === "home") {
      return (
        <HomeExperience
          firstName={firstName}
          checkin={checkin}
          scheduled={scheduled}
          plan={plan}
          stats={stats}
          todayVisits={todayVisits}
          dueFollowups={dueFollowups}
          notifCount={notifCount}
          motivation={motivation}
          onTab={setActiveTab}
          onCheckout={handleCheckout}
          accent={accent}
          loading={loading}
        />
      );
    }
    return (
      <div className="sales-card" style={{ animationDelay: "80ms" }}>
        {tab === "visits" && <AddVisit onDone={() => { setActiveTab("home"); fetchHome(); }} accent={accent} />}
        {tab === "addlead" && <AddLead onDone={() => { setActiveTab("home"); fetchHome(); }} accent={accent} />}
        {tab === "followups" && <FollowUps accent={accent} />}
        {tab === "dayreport" && canSeeTeamDayReport && <DailyVisitReport user={user} accent={accent} teamOnly />}
        {tab === "leads" && <MyLeads accent={accent} />}
        {tab === "map"  && <MapView accent={accent} />}
        {tab === "bulk" && <BulkMessage accent={accent} />}
        {tab === "targets" && <TargetCenter accent={accent} />}
        {tab === "learning" && <SalesLearning accent={accent} />}
        {tab === "profile" && <Profile onBack={() => setActiveTab("home")} />}
        {tab === "productivity" && <DailyProductivity />}
        {tab === "whatsapp" && <WhatsAppCenter user={user} />}
        {tab === "email"    && <EmailCenter    user={user} />}
      </div>
    );
  };

  return (
    <div
      className="sales-shell"
      style={{
        "--sales-red": brand.accent,
        "--sales-teal": brand.secondary,
        "--sales-primary-glow": brand.primaryGlow,
        "--sales-accent-shadow": brand.accentShadow,
      }}
    >
      {sidebarOpen && (
        <div
          className="sales-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:199 }}
        />
      )}
      <aside className={`sales-rail${sidebarOpen ? " open" : ""}`}>
        <div className="sales-brand">
          <img className={`sales-brand-logo ${company === "ebekmed" ? "" : "is-ebek-logo"}`} src={brand.logo} alt={`${brand.label} logo`} />
          <div className="sales-brand-sub">SALES CRM</div>
        </div>
        {/* Context switcher pills for managers/admins in Self view */}
        <CRMContextBanner user={user} inSidebar={true} />

        <nav className="sales-rail-nav">
          {NAV.map(item => (
            <button key={item.key} className={`sales-nav-btn ${tab === item.key ? "active" : ""}`} onClick={() => setActiveTab(item.key)}>
              <span className="sales-nav-icon">{getNavIcon(item)}</span>
              {item.label}
              {item.key === "followups" && dueFollowups.length > 0 && <span className="sales-badge-count">{dueFollowups.length}</span>}
            </button>
          ))}
        </nav>
        <button className="sales-quick-add sales-sidebar-quick-visit" onClick={() => setActiveTab("visits")}>+ Quick Visit </button>
        <AppSwitcher currentApp="crm" allowedApps={allowedApps} />
      </aside>

      <main className="sales-main">
        <header className="sales-topbar">
          <button
            className="sales-hamburger-btn"
            onClick={() => setSidebarOpen(v => !v)}
            aria-label="Open navigation"
            style={{ background:"none", border:"none", color:"#1e293b", fontSize:22, cursor:"pointer",
                     marginRight:10, flexShrink:0 }}
          >
            ☰
          </button>
          <button className="sales-quick-add sales-mobile-quick-visit" onClick={() => setActiveTab("visits")}>+ Quick Visit</button>
          <div className="sales-greeting">
            <h1>Welcome back, {firstName}</h1>
            <p>{motivation}</p>
          </div>
          <div className="sales-target">
            <div className="sales-target-top"><span>Monthly momentum</span><b>{stats?.month_visits || 0} visits</b></div>
            <div className="sales-progress-line"><span /></div>
          </div>
          <div className="sales-user-card" ref={notifRef}>
            <div className="sales-avatar">
              {firstName.slice(0, 1).toUpperCase()}
              {user?.profile_photo && <img src={user.profile_photo} alt="" />}
            </div>
            <div><b>{user?.name}</b><span>{brand.label} Sales</span></div>
            <button className="sales-bell" onClick={() => setNotifOpen(v => !v)}>{notifCount > 0 && <b>{notifCount}</b>}</button>
            <button
              title="My Profile"
              onClick={() => setActiveTab("profile")}
              style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:8,
                       color:"#fff", padding:"6px 10px", cursor:"pointer", fontSize:16, marginLeft:4 }}>
              👤
            </button>

            <button className="sales-logout" onClick={handleLogout}>Logout</button>
            {notifOpen && (
              <NotificationPanel notifications={notifications} notifCount={notifCount} onOpen={openNotification} onRead={markAllRead} />
            )}
          </div>
        </header>

        {renderActiveTab()}
      </main>

      {/* Mobile logout floating button — only when employee is physically checked in (not auto-leave) */}
      {checkin && !checkin.check_out_time && checkin.plan_type !== "planned_leave" && (
        <div className="sales-mobile-logout-bar">
          <button onClick={handleLogout} title="Logout" aria-label="Logout" style={{ pointerEvents: "all", cursor: "pointer" }}>⏻</button>
        </div>
      )}

    </div>
  );
}


function HomeExperience({ firstName, checkin, scheduled, plan, stats, todayVisits, dueFollowups, notifCount, motivation, onTab, onCheckout, accent, loading }) {
  const visitRows = todayVisits.length ? todayVisits.slice(0, 4) : [
    { id: "empty-1", institution_name: "Plan first field visit", contact_name: "Add visit after check-in", lead_status: "warm" },
    { id: "empty-2", institution_name: "Follow up hot leads", contact_name: "Keep the pipeline warm", lead_status: "hot" },
  ];

  return (
    <div className="sales-dashboard-grid">
      <section className="sales-card sales-status-panel">
        <h2 className="sales-card-title">Today Status </h2>
        {loading ? (
          <div className="sales-skeleton">Loading your day...</div>
        ) : checkin ? (
          checkin.plan_type === "planned_leave" ? (
            <>
              <div className="sales-live-pill" style={{ background: "#ede9fe", color: "#7c3aed" }}>
                <span className="sales-live-dot" style={{ background: "#7c3aed" }} /> On Leave Today
              </div>
              <div className="sales-status-work" style={{ color: "#7c3aed" }}> Leave</div>
              <div className="sales-location">Your leave is marked in HRMS. Enjoy your day off!</div>
              <div className="sales-badge-row">
                <span className="sales-mini-badge" style={{ background: "#ede9fe", color: "#7c3aed" }}> Leave</span>
                {notifCount > 0 && <span className="sales-mini-badge amber"> {notifCount} alerts</span>}
              </div>
            </>
          ) : (
            <>
              <div className="sales-live-pill"><span className="sales-live-dot" /> Login time started</div>
              <div className="sales-status-time">{formatTime(checkin.check_in_time)}</div>
              <div className="sales-status-label">Work type</div>
              <div className="sales-status-work">{plan?.emoji} {plan?.label}</div>
              <div className="sales-location"> Attendance synced with HRMS. Keep visits, tasks and follow-ups updated during the day.</div>
              <div className="sales-badge-row">
                <span className="sales-mini-badge green"> Active</span>
                <span className="sales-mini-badge teal"> Streak Day</span>
                {notifCount > 0 && <span className="sales-mini-badge amber"> {notifCount} alerts</span>}
              </div>
              {!checkin.check_out_time && <button className="sales-checkout" onClick={onCheckout}>Log out for today </button>}
            </>
          )
        ) : scheduled ? (
          <>
            <div className="sales-live-pill amber"><span className="sales-live-dot" /> Scheduled</div>
            <div className="sales-status-work"> {PLAN_LABELS[scheduled.event_type]?.label || scheduled.event_type}</div>
            <div className="sales-location">Your day is pre-planned. Mark your attendance in HRMS to start.</div>
            <button className="sales-checkout" onClick={() => {
              const t = localStorage.getItem("hrms_token") || localStorage.getItem("crm_token");
              if (!t) { window.location.href = HRMS_URL + "/login"; return; }
              redirectWithSsoCode({ targetUrl: HRMS_URL, token: t }).catch(() => { window.location.href = HRMS_URL; });
            }}>Mark Attendance in HRMS</button>
          </>
        ) : (
          <>
            <div className="sales-status-work">Ready to start, {firstName}? </div>
            <div className="sales-location">Mark your attendance in HRMS first. Your status will sync here automatically.</div>
            <button className="sales-checkout" onClick={() => {
              const t = localStorage.getItem("hrms_token") || localStorage.getItem("crm_token");
              if (!t) { window.location.href = HRMS_URL + "/login"; return; }
              redirectWithSsoCode({ targetUrl: HRMS_URL, token: t }).catch(() => { window.location.href = HRMS_URL; });
            }}>Mark Attendance in HRMS</button>
          </>
        )}
      </section>

      <section className="sales-card sales-route-card">
        <h2 className="sales-card-title">Live Route Board </h2>
        <div className="sales-map-lines" />
        <div className="sales-route-path" />
        <div className="sales-route-dot one">1</div>
        <div className="sales-route-dot two">2</div>
        <div className="sales-route-dot three">3</div>
        <div className="sales-route-dot four">4</div>
        <div className="sales-route-note">
          <div className="sales-route-stat"><b>{stats?.today_visits || todayVisits.length || 0}</b><span>Visits today </span></div>
          <div className="sales-route-stat"><b>{dueFollowups.length}</b><span>Follow-ups due </span></div>
          <div className="sales-route-stat"><b>{stats?.hot_leads || 0}</b><span>Hot leads </span></div>
        </div>
      </section>

      <section className="sales-card">
        <h2 className="sales-card-title">Badges & Mood </h2>
        <div className="sales-summary-ring"><div><b>{Math.max(1, stats?.today_visits || todayVisits.length || 0)}</b><span>energy score</span></div></div>
        <div className="sales-summary-line"><span>Focus badge</span><b> Route Ready</b></div>
        <div className="sales-summary-line"><span>Pipeline mood</span><b> Warm</b></div>
        <div className="sales-summary-line"><span>In-app note</span><b> PM ping live</b></div>
        <div className="sales-motivation-card"> {motivation}</div>
      </section>

      <section className="sales-card">
        <h2 className="sales-card-title">Visit Queue </h2>
        <div className="sales-list">
          {visitRows.map((v, index) => (
            <div className="sales-list-row" key={v.id || index}>
              <div className="sales-time">{index === 0 ? "Now" : `${10 + index}:30`}</div>
              <div className="sales-row-main"><b>{v.institution_name}</b><span>{v.companions?.length ? `With ${v.companions.map(person => person.name).join(", ")}` : (v.contact_name || "Solo visit")}</span></div>
              <div className={`sales-lead-chip ${v.lead_status || "warm"}`}>{v.lead_status === "hot" ? " Hot" : " Warm"}</div>
            </div>
          ))}
        </div>
        <button className="sales-secondary-action" onClick={() => onTab("visits")}>Add field update +</button>
      </section>

      <section className="sales-card">
        <h2 className="sales-card-title">Follow-up Radar </h2>
        <div className="sales-list">
          {(dueFollowups.length ? dueFollowups.slice(0, 4) : [
            { id: "f1", institution_name: "No urgent follow-up", scheduled_date: todayIST(), status: "clear" },
          ]).map((f, index) => (
            <div className="sales-list-row" key={f.id || index}>
              <div className="sales-time">{f.status === "clear" ? "" : "Due"}</div>
              <div className="sales-row-main"><b>{f.institution_name}</b><span>{f.scheduled_date ? new Date(f.scheduled_date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Today"}</span></div>
              <div className="sales-mini-action">Open</div>
            </div>
          ))}
        </div>
        <button className="sales-secondary-action" onClick={() => onTab("followups")}>Review follow-ups</button>
      </section>

      <section className="sales-card">
        <h2 className="sales-card-title">Hot Leads </h2>
        {[
          { name: "Annai College", detail: "Decision maker call pending" },
          { name: "Muthoot Academy", detail: "Proposal follow-up" },
          { name: "CEQ Exam Desk", detail: "Exam duty lead" },
        ].slice(0, 3).map(item => (
          <div className="sales-hot-row" key={item.name}>
            <div><b>{item.name}</b><span>{item.detail}</span></div><span className="sales-badge-hot">HOT</span>
          </div>
        ))}
        <button className="sales-secondary-action" onClick={() => onTab("leads")}>Open lead board</button>
      </section>

      <section className="sales-card sales-timeline">
        <h2 className="sales-card-title">Day Flow </h2>
        <div className="sales-timeline-track" />
        <div className="sales-timeline-items">
          {["PM asks plan", "Check-in", "First visit", "Follow-up", "Task update", "Logout"].map((item, index) => (
            <div className="sales-timeline-item" key={item}><div className="sales-timeline-pin" /><b>{index + 1}</b>{item}</div>
          ))}
        </div>
      </section>
    </div>
  );
}

function NotificationPanel({ notifications, notifCount, onOpen, onRead }) {
  return (
    <div className="sales-notification-panel">
      <div className="sales-notification-head"><b>Messages </b>{notifCount > 0 && <button onClick={onRead}>Mark all read</button>}</div>
      {notifications.length === 0 ? (
        <div className="sales-empty-message"> All clear. No pending alerts.</div>
      ) : notifications.slice(0, 6).map(note => (
        <button key={note.id} className="sales-message-row" onClick={() => onOpen(note)}>
          <span>{note.icon || ""}</span>
          <div><b>{note.title}</b><small>{note.subtitle}</small></div>
        </button>
      ))}
    </div>
  );
}
